/**
 * Encapsulates the campaign-specific action bar controls (search, filter popover,
 * filter tags, bulk actions, and create button) built on top of DataTableActionBar.
 */
import React, { useRef, useState } from 'react';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Popover from '@amzn/meridian/popover';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import filterIconTokens from '@amzn/meridian-tokens/base/icon/filter';
import { useTranslation } from 'src/arb-wrapper';
import { COUNTRY_OPTIONS, type CampaignFilterState } from 'src/components/common/CampaignFilterBar';
import { DataTableActionBar } from 'src/components/common/data-table-action-bar/DataTableActionBar';
import { type BulkEditMode } from 'src/components/campaigns/BulkEditModal';
import { useCanPerform } from 'src/hooks/useCanPerform';

interface ActiveFilterTag {
  key: string;
  label: string;
  testId: string;
}

export interface CampaignListActionBarProps {
  /** Current local search value */
  localSearch: string;
  /** Setter for local search value */
  onSearchChange: (value: string) => void;
  /** Called on search submit */
  onSearchSubmit: () => void;
  /** Current campaign filter state */
  campaignFilters: CampaignFilterState;
  /** Called when filters change */
  onFiltersChange: (filters: CampaignFilterState) => void;
  /** Available publisher options */
  publisherOptions: string[];
  /** Available business line options */
  businessLineOptions: string[];
  /** Computed active filter tags */
  activeFilterTags: ActiveFilterTag[];
  /** Whether any filters are active (includes search) */
  hasActiveFilters: boolean;
  /** Called to remove a single filter tag */
  onRemoveFilterTag: (key: string) => void;
  /** Called to clear all filters */
  onClearAllFilters: () => void;
  /** Whether bulk controls are shown */
  showBulkControls: boolean;
  /** Number of selected campaigns */
  selectedCount: number;
  /** Called when a bulk edit mode is selected */
  onBulkEditMode: (mode: BulkEditMode) => void;
  /** Called when create campaign is clicked */
  onCreateCampaign: () => void;
}

export const CampaignListActionBar: React.FC<CampaignListActionBarProps> = ({
  localSearch,
  onSearchChange,
  onSearchSubmit,
  campaignFilters,
  onFiltersChange,
  publisherOptions,
  businessLineOptions,
  activeFilterTags,
  hasActiveFilters,
  onRemoveFilterTag,
  onClearAllFilters,
  showBulkControls,
  selectedCount,
  onBulkEditMode,
  onCreateCampaign,
}) => {
  const { t } = useTranslation();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const canManageCampaigns = useCanPerform('MANAGE_CAMPAIGNS');

  return (
    <DataTableActionBar
      actions={canManageCampaigns
        ? (
        <Button
          data-testid="create-campaign-button"
          type="secondary"
          onClick={onCreateCampaign}
        >
          {t('form_campaigns_create')}
        </Button>
          )
        : undefined}
      bulkContent={showBulkControls
        ? (
        <>
          <Text data-testid="bulk-edit-count" type="b200">
            {t('bulk_edit_count', { count: selectedCount })}
          </Text>
          {canManageCampaigns && (
            <>
              <Button
                data-testid="bulk-share-button"
                type="link"
                onClick={() => { onBulkEditMode('share'); }}
              >
                {t('bulk_edit_share')}
              </Button>
              <Button
                data-testid="bulk-blocklist-button"
                type="link"
                onClick={() => { onBulkEditMode('blocklist'); }}
              >
                {t('bulk_edit_blocklist')}
              </Button>
              <Button
                data-testid="bulk-status-button"
                type="link"
                onClick={() => { onBulkEditMode('status'); }}
              >
                {t('bulk_edit_change_status')}
              </Button>
              <Button
                data-testid="bulk-schedule-button"
                type="link"
                onClick={() => { onBulkEditMode('schedule'); }}
              >
                {t('form_program_refresh_schedule')}
              </Button>
            </>
          )}
        </>
          )
        : (
        <Text color="secondary" type="b300">
          {t('select_row_to_view_bulk_actions')}
        </Text>
          )}
      filterButton={(
        <>
          <Button
            aria-expanded={isFilterOpen}
            aria-haspopup="dialog"
            aria-label={t('filter_campaigns_header')}
            data-testid="campaign-filter-button"
            ref={filterButtonRef}
            type="icon"
            onClick={() => { setIsFilterOpen((open) => !open); }}
          >
            <Icon tokens={filterIconTokens}>{t('filter_campaigns_header')}</Icon>
          </Button>
          <Popover
            alignment="start"
            anchorNode={filterButtonRef.current ?? undefined}
            open={isFilterOpen}
            position="bottom"
            onClose={() => { setIsFilterOpen(false); }}
          >
            <Column spacing="300" spacingInset="400">
              <Text tag="span" type="b300"><b>{t('select_filters')}</b></Text>
              <Select
                data-testid="campaign-publisher-filter"
                label={t('publisher')}
                placeholder={t('all')}
                size="small"
                value={campaignFilters.publisher}
                width={200}
                onChange={(publisher: string) => {
                  onFiltersChange({ ...campaignFilters, publisher });
                }}
              >
                <SelectOption key="__all__" label={t('all')} value="" />
                {publisherOptions.map((pub) => (
                  <SelectOption key={pub} label={pub} value={pub} />
                ))}
              </Select>
              <Select
                data-testid="campaign-status-filter"
                label={t('form_global_status')}
                placeholder={t('all')}
                size="small"
                value={campaignFilters.status}
                width={200}
                onChange={(status: string) => {
                  onFiltersChange({ ...campaignFilters, status });
                }}
              >
                <SelectOption key="__all__" label={t('all')} value="" />
                {['ACTIVE', 'PAUSED', 'SUSPENDED', 'ARCHIVED'].map((status) => (
                  <SelectOption key={status} label={status} value={status} />
                ))}
              </Select>
              <Select
                data-testid="campaign-country-filter"
                label={t('country')}
                placeholder={t('all')}
                size="small"
                value={campaignFilters.countries}
                width={200}
                onChange={(selectedCountries: string[]) => {
                  onFiltersChange({ ...campaignFilters, countries: selectedCountries });
                }}
              >
                {COUNTRY_OPTIONS.map((country) => (
                  <SelectOption key={country} label={country} value={country} />
                ))}
              </Select>
              <Select
                data-testid="campaign-businessline-filter"
                label={t('business_line')}
                placeholder={t('all')}
                size="small"
                value={campaignFilters.businessLine}
                width={200}
                onChange={(bl: string) => {
                  onFiltersChange({ ...campaignFilters, businessLine: bl });
                }}
              >
                <SelectOption key="__all__" label={t('all')} value="" />
                {businessLineOptions.map((bl) => (
                  <SelectOption key={bl} label={bl} value={bl} />
                ))}
              </Select>
            </Column>
          </Popover>
        </>
      )}
      filterContent={(
        <>
          {hasActiveFilters
            ? (
            <>
              {activeFilterTags.map((tag) => (
                <Tag
                  data-testid={tag.testId}
                  key={tag.key}
                  type="theme"
                  onClose={() => { onRemoveFilterTag(tag.key); }}
                >
                  {tag.label}
                </Tag>
              ))}
              {localSearch && (
                <Tag
                  data-testid="filter-tag-search"
                  type="theme"
                  onClose={() => { onSearchChange(''); }}
                >
                  {`${t('campaign_name')}: "${localSearch}"`}
                </Tag>
              )}
              <Button
                data-testid="campaign-clear-all-filters"
                type="link"
                onClick={onClearAllFilters}
              >
                {t('clear_filters')}
              </Button>
            </>
              )
            : (
            <Text color="secondary" type="b300">
              {t('no_filters_applied')}
            </Text>
              )}
        </>
      )}
      search={{
        value: localSearch,
        placeholder: t('search_campaigns'),
        testId: 'campaign-name-search',
        width: 350,
        onChange: onSearchChange,
        onSubmit: onSearchSubmit,
      }}
    />
  );
};
